import { useTheme } from "@emotion/react";
import {
  ArrowCircleLeft,
  AutoAwesome,
  Business,
  Close,
  Delete,
  Edit,
  Email,
  ExpandLess,
  ExpandMore,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Language,
  LinkedIn,
  LocationOn,
  People,
  Refresh,
  ReportProblem,
  Save,
  Send,
  VerifiedUser,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ErrorMessage, Form, FormikProvider, useFormik } from "formik";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import cloneDeep from "lodash/cloneDeep";
import { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Page from "src/components/Page";
import TextEditor from "src/components/TextEditor";
import { apiInstance } from "src/utils/apiAuth";
import { capitalizeWords, removeBlankFields } from "src/utils/commonUtils";
import * as constants from "src/utils/constants";
import { fDateTime } from "src/utils/formatTime";
import { showErrorToast, showToast } from "src/utils/Toast/SnackbarProvider";
import * as Yup from "yup";

// Register the English locale for country names
countries.registerLocale(enLocale);

const mobileRegExp = /^[+]?[0-9\s\-()]{7,20}$/;

// Dynamic helper function to get ISO country code from country name
const getCountryISOCode = (countryName) => {
  if (!countryName) return "in";

  // Try to get alpha-2 code directly by name
  const code = countries.getAlpha2Code(countryName, "en");

  if (code) return code.toLowerCase();

  // If direct lookup fails, try fuzzy matching
  const normalized = countryName.toLowerCase().trim();
  const allCountries = countries.getNames("en");

  // Find country by partial match
  const match = Object.entries(allCountries).find(
    ([code, name]) =>
      name.toLowerCase() === normalized ||
      name.toLowerCase().includes(normalized) ||
      normalized.includes(name.toLowerCase())
  );

  return match ? match[0].toLowerCase() : "in";
};

// Utility function to extract percentage from stage name
const getStagePercentage = (stageName) => {
  if (!stageName) return 0;
  const match = stageName.match(/(\d+)%/);
  return match ? parseInt(match[1]) : 0;
};

// Utility function to check if company is eligible for enrichment
// Now available for all companies regardless of stage
const isEligibleForEnrichment = (currentStageId, stages) => {
  // Return true if we have a valid stage ID - enrichment is now available for all companies
  return !!currentStageId;
};

const isCreatedByAgent = (company) => {
  return (
    company?.created_by?.first_name === "GWX" &&
    company?.created_by?.last_name === "Agent"
  );
};

const ExpandableText = ({ text, limit = 300 }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  if (text.length <= limit) {
    return (
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.6, whiteSpace: "pre-wrap" }}
      >
        {text}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.6, whiteSpace: "pre-wrap" }}
      >
        {expanded ? text : `${text.substring(0, limit)}...`}
        <Link
          component="button"
          variant="body2"
          onClick={() => setExpanded(!expanded)}
          sx={{
            ml: 1,
            fontWeight: 600,
            textDecoration: "none",
            color: "#1976d2",
            cursor: "pointer",
            verticalAlign: "baseline",
            fontSize: "0.875rem",
          }}
        >
          {expanded ? "Show Less" : "Show More"}
        </Link>
      </Typography>
    </Box>
  );
};

export default function AddCompany({
  id,
  setOpenDrawer,
  companyData: propsCompanyData,
  stages = [],
  onCompanyUpdate,
  defaultTab,
}) {
  const hrToken = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  // State for phone country code
  const [phoneCountryCode, setPhoneCountryCode] = useState("in");

  // State for company member phone country code
  const [memberPhoneCountryCode, setMemberPhoneCountryCode] = useState("in");

  // State to force member PhoneInput remount
  const [memberPhoneInputKey, setMemberPhoneInputKey] = useState(Date.now());

  // State to force PhoneInput remount
  const [phoneInputKey, setPhoneInputKey] = useState(Date.now());

  // View mode state (0: Normal, 1: AI Enrichment, 2: Pitch)
  const [viewMode, setViewMode] = useState(0);

  // Enrichment sections expansion state
  const [expandedSections, setExpandedSections] = useState({
    suborganizations: false,
    funding: false,
    people: true,
  });

  // People section pagination
  const [peopleDisplayCount, setPeopleDisplayCount] = useState(10);

  // Track added members to prevent duplicates
  const [addedMemberEmails, setAddedMemberEmails] = useState(new Set());

  // Enrichment loading state
  const [isEnriching, setIsEnriching] = useState(false);

  // Perplexity enrichment loading state
  const [isPerplexityEnriching, setIsPerplexityEnriching] = useState(false);

  // Pitch Tab States
  const [pitchText, setPitchText] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [isSavingPitch, setIsSavingPitch] = useState(false);
  const [isSendingPitch, setIsSendingPitch] = useState(false);
  const [selectedSender, setSelectedSender] = useState("");

  // Pitch History State
  const [pitchHistory, setPitchHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // Preview Dialog State
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

  // Outreach details state
  const [outreachDetails, setOutreachDetails] = useState(null);
  const [isLoadingOutreach, setIsLoadingOutreach] = useState(false);

  // Duplicate check state
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  // Autocomplete suggestions state
  const [companyNameSuggestions, setCompanyNameSuggestions] = useState([]);
  const [websiteSuggestions, setWebsiteSuggestions] = useState([]);
  const [isLoadingNameSuggestions, setIsLoadingNameSuggestions] =
    useState(false);
  const [isLoadingWebsiteSuggestions, setIsLoadingWebsiteSuggestions] =
    useState(false);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  /*****************GET MASTER VALUES *****************************/
  const [senderDetails, setSenderDetails] = useState([]);
  const getSenderDetails = () => {
    let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/sender-details?`;

    apiInstance
      .get(apiUrl, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setSenderDetails(response?.data?.data?.filter((obj) => obj?.is_active));
      })
      .catch((e) => showErrorToast(e));
  };

  const [cmds, setCMD] = useState([]);
  const getCMDData = () => {
    let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company-member-designation?`;

    apiInstance
      .get(apiUrl, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setCMD(response?.data?.data?.filter((obj) => obj?.is_active));
      })
      .catch((e) => showErrorToast(e));
  };

  const [usersData, setUsersData] = useState([]);
  const getUsersData = () => {
    const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/users-viewset?short_details=true`;
    apiInstance
      .get(apiUrl, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        if (response?.data) {
          setUsersData(response?.data?.data || []);
        }
      })
      .catch((error) => {
        if (error) {
          showErrorToast(error);
        }
      });
  };

  // Partners for the "Introduced by a partner?" picker (C2). Selecting a partner
  // auto-fills its default BA/BM (FR-004).
  const [partnersData, setPartnersData] = useState([]);
  const getPartnersData = () => {
    const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/partner/`;
    apiInstance
      .get(apiUrl, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setPartnersData(response?.data?.data || []);
      })
      .catch((error) => {
        if (error) {
          showErrorToast(error);
        }
      });
  };

  // Whether the "Introduced by a partner?" toggle is on. When off, no
  // partner/ba/bm keys are sent in the payload (preserves pre-C2 behavior).
  const [isPartnerIntroduced, setIsPartnerIntroduced] = useState(false);

  const [stagesData, setStagesData] = useState([]);
  const getStagesData = () => {
    const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/get_stage_names`;
    apiInstance
      .get(apiUrl, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        if (response?.data) {
          setStagesData(response?.data?.data || []);
        }
      })
      .catch((error) => {
        if (error) {
          showErrorToast(error);
        }
      });
  };

  const [locationData, setLocationData] = useState([]);
  const [businessTypeData, setBusinessTypeData] = useState([]);
  const [offeringData, setOfferingData] = useState([]);
  const [segmentData, setSegmentData] = useState([]);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [locationLoadError, setLocationLoadError] = useState(false);
  const getCountryData = () => {
    const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/location?`;
    setIsLocationLoading(true);
    setLocationLoadError(false);
    apiInstance
      .get(apiUrl, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        if (response?.data) {
          setLocationData(
            response?.data?.data?.filter((obj) => obj?.is_active) || []
          );
        }
      })
      .catch((error) => {
        setLocationLoadError(true);
        if (error) {
          showErrorToast(error);
        }
      })
      .finally(() => {
        setIsLocationLoading(false);
      });
  };

  const getBusinessTypeData = () => {
    const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/business-type?`;
    apiInstance
      .get(apiUrl, {
        headers: { Authorization: `Token ${localStorage.getItem("authToken")}` },
      })
      .then((response) => {
        if (response?.data) {
          setBusinessTypeData(response?.data?.data?.filter((o) => o?.is_active) || []);
        }
      })
      .catch((error) => {
        if (error) {
          showErrorToast(error);
        }
      });
  };

  const getOfferingData = () => {
    const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/offering/?active_only=true`;
    apiInstance
      .get(apiUrl, {
        headers: { Authorization: `Token ${localStorage.getItem("authToken")}` },
      })
      .then((response) => {
        if (response?.data) {
          setOfferingData(response?.data?.data?.filter((o) => o?.is_active) || []);
        }
      })
      .catch((error) => {
        if (error) {
          showErrorToast(error);
        }
      });
  };

  // Fetch the segments belonging to the chosen offering (2-level Offering → Segment cascade).
  const getSegmentData = (offeringId) => {
    if (!offeringId) {
      setSegmentData([]);
      return;
    }
    const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/segment/?offering_id=${offeringId}&active_only=true`;
    apiInstance
      .get(apiUrl, {
        headers: { Authorization: `Token ${localStorage.getItem("authToken")}` },
      })
      .then((response) => {
        if (response?.data) {
          setSegmentData(response?.data?.data?.filter((o) => o?.is_active) || []);
        }
      })
      .catch((error) => {
        if (error) {
          showErrorToast(error);
        }
      });
  };

  // Stage assignment state
  const [stageAssignments, setStageAssignments] = useState({
    stage: "",
    lead: "",
    sam: "",
    pm: "",
    legal: "",
    dm: "",
  });

  useEffect(() => {
    getCMDData();
    getUsersData();
    getStagesData();
    getCountryData();
    getBusinessTypeData();
    getOfferingData();
    getSenderDetails();
    getPartnersData();
  }, []);
  /****************************************************************/

  /*****************GET Company Details for Edit ******************/
  const [companyData, setCompanyData] = useState(propsCompanyData || null);
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(!!id);
  const [companyNotFound, setCompanyNotFound] = useState(false);

  // When editing a company that already has an offering, load its segments so the
  // Segment selector can resolve the pre-filled value.
  useEffect(() => {
    if (companyData?.offering?.id) {
      getSegmentData(companyData.offering.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData?.offering?.id]);

  // Pre-fill the partner toggle when editing a company that already has a
  // partner. enableReinitialize re-seeds the partner/ba/bm formik values.
  useEffect(() => {
    if (companyData?.partner) {
      setIsPartnerIntroduced(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData?.partner]);
  const getCompanydata = () => {
    if (!id) return;
    // Always fetch from API to ensure we have complete data including company_members
    setIsLoadingCompanyData(true);
    setCompanyNotFound(false);
    const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}?`;
    apiInstance
      .get(apiUrl, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        if (response?.data) {
          setCompanyData(response?.data?.data || null);
          // Force PhoneInput remount when data is loaded
          if (response?.data?.data?.contact_number) {
            setTimeout(() => setPhoneInputKey(Date.now()), 0);
          }
        }
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          setCompanyNotFound(true);
        } else if (error) {
          showErrorToast(error);
        }
      })
      .finally(() => {
        setIsLoadingCompanyData(false);
      });
  };

  useEffect(() => {
    if (id) {
      getCompanydata();
      // Force PhoneInput remount when opening a different company
      setPhoneInputKey(Date.now());
    }
  }, [id]);

  // Update phone country code when company data is loaded or country changes
  useEffect(() => {
    if (companyData) {
      if (companyData?.country?.label) {
        const isoCode = getCountryISOCode(companyData.country.label);
        setPhoneCountryCode(isoCode);
      }

      // Force PhoneInput to remount when contact_number changes
      if (companyData?.contact_number) {
        setPhoneInputKey(Date.now());
      }

      // Initialize pitch text
      if (companyData?.pitch_line) {
        setPitchText(companyData.pitch_line);
      }

      // Handle default tab selection
      if (defaultTab === "pitch" && isCreatedByAgent(companyData)) {
        setViewMode(2);
      }
    }
  }, [companyData, defaultTab]);

  // Fetch outreach details for 0% stage companies
  const fetchOutreachDetails = async () => {
    if (!id) return;

    // Check if company is at 0% stage
    let stageName =
      companyData?.current_stage?.name ||
      companyData?.current_stage?.label ||
      companyData?.current_stage;
    // Ensure stageName is a string
    if (typeof stageName !== "string") {
      stageName = "";
    }
    const isZeroPercentStage =
      stageName &&
      (stageName.toLowerCase().includes("- 0%") ||
        stageName.toLowerCase().includes("outreach - 0") ||
        stageName.toLowerCase() === "outreach - 0%");

    if (!isZeroPercentStage) {
      setOutreachDetails(null);
      return;
    }

    setIsLoadingOutreach(true);
    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/outreach-details/`,
        { headers: { Authorization: `Token ${hrToken}` } }
      );
      if (response.data.success) {
        setOutreachDetails(response.data.data);
      }
    } catch (error) {
      // Company not outreached - this is expected
      setOutreachDetails(null);
    } finally {
      setIsLoadingOutreach(false);
    }
  };

  useEffect(() => {
    if (id && companyData) {
      fetchOutreachDetails();
    }
  }, [id, companyData]);
  /****************************************************************/

  /***************** Duplicate Check *******************************/
  const checkForDuplicate = async (companyName, website) => {
    // Only check if we have at least company name or website
    if (!companyName && (!website || website === "https://")) {
      setDuplicateWarning(null);
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const response = await apiInstance.post(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/check-duplicate/`,
        {
          company_name: companyName || "",
          website: website || "",
          exclude_id: id || null, // Exclude current company when editing
        },
        { headers: { Authorization: `Token ${hrToken}` } }
      );

      if (response.data?.is_duplicate) {
        setDuplicateWarning(response.data.duplicates);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error("Duplicate check error:", error);
      // Don't show error to user, just clear warning
      setDuplicateWarning(null);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  // Debounced duplicate check
  const [duplicateCheckTimeout, setDuplicateCheckTimeout] = useState(null);
  const debouncedDuplicateCheck = (companyName, website) => {
    if (duplicateCheckTimeout) {
      clearTimeout(duplicateCheckTimeout);
    }
    const timeout = setTimeout(() => {
      checkForDuplicate(companyName, website);
    }, 500); // 500ms debounce
    setDuplicateCheckTimeout(timeout);
  };

  // Autocomplete search for company name
  const [nameSearchTimeout, setNameSearchTimeout] = useState(null);
  const searchCompanyNames = async (query) => {
    if (!query || query.length < 2) {
      setCompanyNameSuggestions([]);
      return;
    }

    setIsLoadingNameSuggestions(true);
    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/autocomplete-search/`,
        {
          params: {
            type: "name",
            q: query,
            exclude_id: id || null,
          },
          headers: { Authorization: `Token ${hrToken}` },
        }
      );
      setCompanyNameSuggestions(response.data?.results || []);
    } catch (error) {
      console.error("Company name search error:", error);
      setCompanyNameSuggestions([]);
    } finally {
      setIsLoadingNameSuggestions(false);
    }
  };

  const debouncedNameSearch = (query) => {
    if (nameSearchTimeout) {
      clearTimeout(nameSearchTimeout);
    }
    const timeout = setTimeout(() => {
      searchCompanyNames(query);
    }, 300);
    setNameSearchTimeout(timeout);
  };

  // Autocomplete search for website
  const [websiteSearchTimeout, setWebsiteSearchTimeout] = useState(null);
  const searchWebsites = async (query) => {
    if (!query || query.length < 5 || query === "https://") {
      setWebsiteSuggestions([]);
      return;
    }

    setIsLoadingWebsiteSuggestions(true);
    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/autocomplete-search/`,
        {
          params: {
            type: "website",
            q: query,
            exclude_id: id || null,
          },
          headers: { Authorization: `Token ${hrToken}` },
        }
      );
      setWebsiteSuggestions(response.data?.results || []);
    } catch (error) {
      console.error("Website search error:", error);
      setWebsiteSuggestions([]);
    } finally {
      setIsLoadingWebsiteSuggestions(false);
    }
  };

  const debouncedWebsiteSearch = (query) => {
    if (websiteSearchTimeout) {
      clearTimeout(websiteSearchTimeout);
    }
    const timeout = setTimeout(() => {
      searchWebsites(query);
    }, 300);
    setWebsiteSearchTimeout(timeout);
  };
  /****************************************************************/

  /***************** Company Formik *******************************/
  const callAddCompany = (formValues) => {
    var apiData = removeBlankFields({
      company_name: formValues.company_name,
      website: formValues.website,
      company_members: formValues.company_members,
      country: formValues.country,
      business_type: formValues.business_type,
      offering: formValues.offering,
      segment: formValues.segment,
      practice: formValues.practice,
      email: formValues.email,
      contact_number: formValues.contact_number,
      total_contract_value: formValues.total_contract_value,
      business_description: formValues.business_description,
      stage_assignments: stageAssignments,
      // Partner section (C2): only send partner/ba/bm when the toggle is on and a
      // partner is selected, so nothing partner-related leaks into the payload
      // when off (preserves pre-C2 behavior, FR-008).
      ...(isPartnerIntroduced && formValues.partner
        ? {
            partner: formValues.partner,
            ...(formValues.ba ? { ba: formValues.ba } : {}),
            ...(formValues.bm ? { bm: formValues.bm } : {}),
          }
        : {}),
    });

    apiInstance({
      method: "post",
      url: "company/",
      data: apiData,
    })
      .then(function (response) {
        if (response) {
          showToast(response?.data?.message);
          if (setOpenDrawer) {
            setOpenDrawer(false);
            window.location.reload(false);
          } else {
            navigate("/companies");
          }
        }
      })
      .catch(function (error) {
        showErrorToast(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const callUpdateCompany = async (formValues) => {
    var apiData = removeBlankFields({
      company_name: formValues.company_name,
      website: formValues.website,
      company_members: formValues.company_members,
      country: formValues.country,
      business_type: formValues.business_type,
      offering: formValues.offering,
      segment: formValues.segment,
      total_contract_value: formValues.total_contract_value,
      practice: formValues.practice,
      email: formValues.email,
      contact_number: formValues.contact_number,
      business_description: formValues.business_description,
      stage_assignments: stageAssignments,
      // Partner section (C2): only send partner/ba/bm when the toggle is on and a
      // partner is selected, so nothing partner-related leaks into the payload
      // when off (preserves pre-C2 behavior, FR-008).
      ...(isPartnerIntroduced && formValues.partner
        ? {
            partner: formValues.partner,
            ...(formValues.ba ? { ba: formValues.ba } : {}),
            ...(formValues.bm ? { bm: formValues.bm } : {}),
          }
        : {}),
    });

        await apiInstance({
            method: "put",
            url: "company/" + id + "/",
            headers: {
                Authorization: "token " + hrToken,
                
            },
            data: apiData,
        })
            .then(function (response) {
                if (response) {
                    showToast(response?.data?.message);
                    navigate(`/companies/${id}`);
                    setOpenDrawer(false);
                    window.location.reload(false);
                }
            })
            .catch(function (error) {
                console.error('Update Company Error:', error?.response?.data || error);
                const errorMsg = error?.response?.data?.detail || 
                                 error?.response?.data?.message || 
                                 error?.message || 
                                 'Failed to update company';
                showErrorToast(errorMsg);
            })
            .finally(() => {
                setSubmitting(false);
            });
    }

  const CompanySchema = Yup.object().shape({
    company_name: Yup.string()
      .max(50, "Company name must be at most 50 characters")
      .required("Company name is required"),
    website: Yup.string()
      .url("Invalid URL format")
      .required("Website is required"),
    country: Yup.string().test(
      "country-required",
      "Country is required",
      (value) => {
        if (locationData?.length === 0) return true;
        return !!value;
      }
    ),
    // A2 (item #5): Offering, Business Type and Segment are mandatory on company
    // create only. On edit (id present) they stay optional so legacy companies that
    // predate these fields can still be saved.
    business_type: id
      ? Yup.string()
      : Yup.string().required("Business Type is required"),
    offering: id
      ? Yup.string()
      : Yup.string().required("Offering is required"),
    segment: id
      ? Yup.string()
      : Yup.string().required("Segment is required"),
    practice: Yup.string(),
    email: Yup.string().email("Invalid email format"),
    contact_number: Yup.string()
      .nullable()
      .notRequired()
      .test("mobile-valid", "Mobile number is not valid", function (value) {
        if (!value || value === "") return true; // Allow empty
        return mobileRegExp.test(value);
      }),

    business_description: Yup.string().max(
      1000,
      "Business Description must be at most 1000 characters"
    ),
    company_members: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.mixed(), // Allow number or empty string for new members
          first_name: Yup.string()
            .min(2, "Too Short!")
            .max(50, "Too Long!")
            .required("First name required"),
          last_name: Yup.string()
            .min(2, "Too Short!")
            .max(50, "Too Long!")
            .required("Last name required"),
          // Temporary bypass: on edit (id present) member email is optional so
          // legacy/backfilled companies whose members have no email can still be
          // saved (e.g. when setting Offering/Segment/Business Type). On create
          // it stays required. Mirrors the A2 edit-optional pattern above.
          email: id
            ? Yup.string().email("Invalid email format")
            : Yup.string().email("Invalid email format").required("Email is required"),
          mobile: Yup.string(),
          linkedin_profile: Yup.string(),
          cmd: Yup.array(),
          // is_active: Yup.boolean()
        })
      )
      .min(1, "At least one company member is required"),
  });

  const formik = useFormik({
    initialValues: {
      company_name: companyData?.company_name || "",
      website: companyData?.website || "https://",
      country: companyData?.country?.id || "",
      business_type: companyData?.business_type?.id || "",
      offering: companyData?.offering?.id || "",
      segment: companyData?.segment?.id || "",
      practice: companyData?.practice?.id || "",
      email: companyData?.email || "",
      contact_number: companyData?.contact_number || "",
      total_contract_value: companyData?.total_contract_value || "",
      business_description: companyData?.business_description || "",
      partner: companyData?.partner?.id || "",
      ba: companyData?.partner?.ba || "",
      bm: companyData?.partner?.bm || "",
      company_members:
        companyData?.company_members?.map((member) => ({
          id: member?.id || "",
          first_name: member?.first_name || "",
          last_name: member?.last_name || "",
          email: member?.email || "",
          country: member?.country || companyData?.country?.id || "",
          mobile: member?.mobile || "",
          linkedin_profile: member?.linkedin_profile || "",
          cmd: member?.cmd?.map((obj) => obj?.id) || "",
          // is_active: member.is_active || false,
        })) || [],
    },
    enableReinitialize: true,
    validationSchema: CompanySchema,
    onSubmit: (values) => {
      if (id) {
        callUpdateCompany(values);
      } else {
        callAddCompany(values);
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    setFieldValue,
  } = formik;
  /****************************************************************/

  /***************** Company Member Form **************************/
  const CompanyMemberSchema = Yup.object().shape({
    id: Yup.number(),
    first_name: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .matches(
        /^[a-zA-Z]+$/,
        "Only alphabets without spaces are allowed for first name"
      )
      .required("First name required"),
    last_name: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .matches(
        /^[a-zA-Z]+$/,
        "Only alphabets without spaces are allowed for last name"
      )
      .required("Last name required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    country: Yup.string(),
    mobile: Yup.string(),
    linkedin_profile: Yup.string(),
    cmd: Yup.array(),
    // is_active: Yup.boolean()
  });

  const companyMemberFormik = useFormik({
    initialValues: {
      id: "",
      first_name: "",
      last_name: "",
      email: "",
      country: "",
      mobile: "",
      linkedin_profile: "",
      cmd: [],
      status: "",
      // is_active: true
    },
    validationSchema: CompanyMemberSchema,
    onSubmit: (values) => {
      handleCompanyMemberSubmit(values);
    },
  });

  const [editIndex, setEditIndex] = useState(null);

  // Sync member phone country code with company country when member country is not selected
  useEffect(() => {
    if (!companyMemberFormik.values.country) {
      setMemberPhoneCountryCode(phoneCountryCode);
    }
  }, [phoneCountryCode, companyMemberFormik.values.country]);

  // Update member phone country code when member's country changes
  useEffect(() => {
    if (companyMemberFormik.values.country && locationData.length > 0) {
      const countryLabel = locationData.find(
        (c) => c.id === companyMemberFormik.values.country
      )?.label;
      if (countryLabel) {
        const isoCode = getCountryISOCode(countryLabel);
        setMemberPhoneCountryCode(isoCode);
        // Force remount to reflect the new country code
        setMemberPhoneInputKey(Date.now());
      }
    }
  }, [companyMemberFormik.values.country, locationData]);

  // Set default country for new members to company's country
  useEffect(() => {
    // Only set default country when not editing a member and company country is set
    if (
      editIndex === null &&
      formik.values.country &&
      !companyMemberFormik.values.country
    ) {
      companyMemberFormik.setFieldValue("country", formik.values.country);
    }
  }, [formik.values.country, editIndex]);
  const handleCompanyMemberSubmit = (values) => {
    if (editIndex !== null) {
      const updatedCompanyMembers = [...formik.values.company_members];
      updatedCompanyMembers[editIndex] = values;
      formik.setFieldValue("company_members", updatedCompanyMembers);
    } else {
      formik.setFieldValue("company_members", [
        ...formik.values.company_members,
        values,
      ]);
    }

    handleCompanyMemberReset();
  };

  const handleCompanyMemberReset = () => {
    companyMemberFormik.handleReset();
    setEditIndex(null);
    // Reset member phone country code to company's country code
    setMemberPhoneCountryCode(phoneCountryCode);
    // Force member PhoneInput remount
    setMemberPhoneInputKey(Date.now());
  };

  const handleEditClick = (index) => {
    setEditIndex(index);
    const memberToEdit = cloneDeep(formik.values.company_members[index]);
    // If member doesn't have a country, default to company's country
    if (!memberToEdit.country) {
      memberToEdit.country = formik.values.country;
    }
    setCompanyMemberEditValues(memberToEdit);
  };

  const setCompanyMemberEditValues = (values) => {
    values.status = "EDITED";
    companyMemberFormik.setValues(values);
    // Update member phone country code when editing
    if (values.country) {
      const countryLabel = locationData.find(
        (c) => c.id === values.country
      )?.label;
      if (countryLabel) {
        const isoCode = getCountryISOCode(countryLabel);
        setMemberPhoneCountryCode(isoCode);
      }
    } else {
      // If no country set, use company's country
      setMemberPhoneCountryCode(phoneCountryCode);
    }
    // Force member PhoneInput remount to show the correct number
    setMemberPhoneInputKey(Date.now());
  };

  const handleDeleteClick = (index) => {
    const updatedCompanyMembers = JSON.parse(
      JSON.stringify(formik.values.company_members)
    );
    if (id) {
      updatedCompanyMembers[index].status = "DELETED";
    } else {
      updatedCompanyMembers.splice(index, 1);
    }
    formik.setFieldValue("company_members", updatedCompanyMembers);

    if (editIndex === index) setEditIndex(null);
  };

  const remainingMembers = formik.values.company_members?.filter(
    (m) => m.status !== "DELETED"
  );

  const handleNameChange = (field) => (event) => {
    const capitalizedValue = capitalizeWords(event.target.value);
    companyMemberFormik.setFieldValue(field, capitalizedValue);
  };
  /****************************************************************/

  /******************* Enrich Company with Apollo *****************/
  const handleEnrichCompany = async () => {
    if (!id) {
      showErrorToast("Company ID is required for enrichment.");
      return;
    }

    if (isEnriching) return; // prevent multiple clicks

    try {
      setIsEnriching(true);
      const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/enrich/`;

      // Trigger enrichment process
      await apiInstance.post(
        apiUrl,
        {},
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
          },
        }
      );

      // Show processing message
      showToast("Enrichment process started. Fetching updated data...", "info");

      // Wait for 3 seconds for backend to process enrichment
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Fetch updated company data
      const companyApiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}?`;
      const companyResponse = await apiInstance.get(companyApiUrl, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      });

      if (companyResponse?.data?.data) {
        const enrichedData = companyResponse.data.data;

        // Update company data with enriched information
        setCompanyData(enrichedData);

        // Call parent component's update handler if provided
        if (onCompanyUpdate) {
          onCompanyUpdate(enrichedData);
        }

        // Check if enrichment was successful
        if (enrichedData.apollo_enrichment_fetched) {
          // Switch to enrichment view automatically
          setViewMode(1);
          showToast("Company enriched successfully with Apollo data!");
        } else {
          showToast(
            "Enrichment is in progress. Please refresh after a few moments."
          );
        }
      } else {
        showErrorToast(
          "Failed to fetch updated company data. Please refresh the page."
        );
      }
    } catch (error) {
      // More specific error message
      if (error?.response?.data) {
        const message =
          error.response.data.error ||
          error.response.data.detail ||
          error.response.data.message ||
          JSON.stringify(error.response.data);
        showErrorToast(`Enrichment Error: ${message}`);
      } else {
        showErrorToast("Network or server error while enriching company.");
      }
    } finally {
      setIsEnriching(false);
    }
  };

  /******************* Pitch Logic *****************/
  const handlePitchSave = async () => {
    try {
      setIsSavingPitch(true);
      await apiInstance.put(
        `company/${id}/`,
        { pitch_line: pitchText },
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
          },
        }
      );
      showToast("Pitch updated successfully", "success");
      if (onCompanyUpdate) {
        onCompanyUpdate({ ...companyData, pitch_line: pitchText });
      }
    } catch (error) {
      showErrorToast("Error updating pitch");
    } finally {
      setIsSavingPitch(false);
    }
  };

  const getPitchHistory = async () => {
    if (!id) return;
    try {
      setIsHistoryLoading(true);
      const response = await apiInstance.get(
        `pitch-delivery-tracking/?company_id=${id}`,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
          },
        }
      );
      if (response.data?.success) {
        setPitchHistory(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching pitch history:", error);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 2 && isCreatedByAgent(companyData)) {
      getPitchHistory();
    }
  }, [viewMode, id]);

  const handleSendPitch = async () => {
    if (selectedMemberIds.length === 0) {
      showErrorToast("Please select at least one member");
      return;
    }

    if (!selectedSender) {
      showErrorToast("Please select a sender");
      return;
    }

    try {
      setIsSendingPitch(true);
      const payload = {
        company_id: id,
        company_member_ids: selectedMemberIds,
        sender_id: selectedSender,
      };

      await apiInstance.post("pitch-delivery-tracking/send-pitch/", payload, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      });

      showToast("Pitch email is being sent", "success");
      setSelectedMemberIds([]);
      setSelectedSender("");
      getPitchHistory();
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Error sending pitch");
    } finally {
      setIsSendingPitch(false);
    }
  };

  const EnrichmentSectionHeader = ({ title, icon: Icon, section, count }) => (
    <Box
      onClick={() => toggleSection(section)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.5,
        cursor: "pointer",
        backgroundColor: "#fafafa",
        borderRadius: 1,
        mb: 1,
        "&:hover": { backgroundColor: "#f5f5f5" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Icon fontSize="small" sx={{ color: "#1976d2" }} />
        <Typography
          variant="subtitle2"
          component="span"
          sx={{ fontWeight: 600, display: "flex", alignItems: "center" }}
        >
          {title}
          {count && (
            <Chip
              label={count}
              size="small"
              sx={{ ml: 1, height: 18, fontSize: "0.7rem" }}
            />
          )}
        </Typography>
      </Box>
      <IconButton size="small">
        {expandedSections[section] ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
      </IconButton>
    </Box>
  );

  const [addingMemberEmail, setAddingMemberEmail] = useState(null); // Track which member is being added

  const handleAddApolloMember = async (person) => {
    if (!person?.email || person?.email_status !== "verified") {
      showErrorToast("Only verified members with email can be added.");
      return;
    }

    // Check if already exists in company_members (including from API)
    const existingMember = formik.values.company_members?.find(
      (m) =>
        m.email?.toLowerCase() === person.email?.toLowerCase() &&
        m.status !== "DELETED"
    );
    if (existingMember) {
      showErrorToast("This member already exists in company members.");
      return;
    }

    // Check if already added in this session
    if (addedMemberEmails.has(person.email)) {
      showErrorToast("This member has already been added.");
      return;
    }

    // Get country ID (formik.values.country might be object or ID)
    const countryId =
      typeof formik.values.country === "object"
        ? formik.values.country?.id
        : formik.values.country;

    const newMember = {
      id: "", // new member
      first_name: person.first_name || "",
      last_name: person.last_name || "",
      email: person.email,
      country: countryId || "",
      mobile: "",
      linkedin_profile: person.linkedin_url || "",
      cmd: [],
      status: "", // new member
    };

    // Set loading state for this member
    setAddingMemberEmail(person.email);

    try {
      // Directly save to database by calling update API
      const updatedMembers = [...formik.values.company_members, newMember];
      const apiData = removeBlankFields({
        company_name: formik.values.company_name,
        website: formik.values.website,
        company_members: updatedMembers,
        country: formik.values.country,
        practice: formik.values.practice,
      });

      await apiInstance({
        method: "put",
        url: "company/" + id + "/",
        headers: {
          Authorization: "token " + hrToken,
        },
        data: apiData,
      });

      // Update local state after successful API call
      formik.setFieldValue("company_members", updatedMembers);
      setAddedMemberEmails((prev) => new Set([...prev, person.email]));

      // Refresh company data to get the new member with proper ID
      getCompanydata();

      showToast(
        `${person.first_name} ${person.last_name} added to company members`
      );
    } catch (error) {
      console.error("Add Member Error:", error?.response?.data || error);
      const errorMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add member";
      showErrorToast(errorMsg);
    } finally {
      setAddingMemberEmail(null);
    }
  };

  return (
    <Page title={id ? "Update Company" : "Add Company"}>
      <Container
        maxWidth="xl"
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "sticky",
        }}
      >
        <Stack
          direction="row"
          sx={{
            mb: 1,
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
          }}
        >
          {setOpenDrawer ? (
            <IconButton onClick={() => setOpenDrawer(false)}>
              <Close fontSize={"medium"} />
            </IconButton>
          ) : (
            <Link
              to="/dashboard"
              color="green"
              underline="hover"
              component={RouterLink}
              fontSize="20px"
            >
              <ArrowCircleLeft fontSize="large" />
            </Link>
          )}
          <Typography
            variant="h4"
            sx={{ my: matches ? 0 : 1 }}
            align="center"
            color="black"
          >
            {id ? "Update Company" : "Add Company"}
          </Typography>
          <div></div>
        </Stack>

        {/* Loading State */}
        {id && isLoadingCompanyData && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading company details...
            </Typography>
          </Box>
        )}

        {/* Company Not Found Error */}
        {id && companyNotFound && !isLoadingCompanyData && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              backgroundColor: "#fff3e0",
              borderRadius: 2,
              border: "1px solid #ff9800",
            }}
          >
            <ReportProblem sx={{ fontSize: 48, color: "#ff9800", mb: 2 }} />
            <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
              Company Not Found
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 400 }}
            >
              This company may have been deleted or you don't have permission to
              view it.
            </Typography>
          </Box>
        )}

        {/* Enrichment Trigger Button - Show when stage >= 10% and no enrichment data */}
        {id &&
          !isLoadingCompanyData &&
          !companyNotFound &&
          !companyData?.apollo_enrichment_fetched &&
          isEligibleForEnrichment(
            companyData?.current_stage || companyData?.current_stage_id,
            stages
          ) && (
            <Box
              sx={{
                mb: 1,
                p: 2,
                backgroundColor: "#e3f2fd",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ mb: 1.5, color: "#1976d2", fontWeight: 500 }}
              >
                This company is eligible for AI enrichment with Apollo.io
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutoAwesome />}
                onClick={handleEnrichCompany}
                disabled={isEnriching}
                sx={{
                  backgroundColor: "#1976d2",
                  "&:hover": { backgroundColor: "#1565c0" },
                }}
              >
                {isEnriching ? "Enriching..." : "Enrich with Apollo"}
              </Button>
            </Box>
          )}

        {/* Outreach Details - Show for 0% stage companies */}
        {id && outreachDetails && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: "#e8f5e9",
              borderRadius: 2,
              border: "1px solid #4caf50",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <span style={{ fontSize: "1.2rem" }}>✅</span>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#2e7d32" }}
              >
                Outreach Completed
              </Typography>
            </Box>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="body2">
                  <strong>Method:</strong>{" "}
                  {outreachDetails.outreach_method_display}
                </Typography>
                <Typography variant="body2">
                  <strong>By:</strong> {outreachDetails.marked_by_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong>{" "}
                  {new Date(outreachDetails.outreached_at).toLocaleDateString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: "white",
                  p: 1.5,
                  borderRadius: 1,
                  mt: 1,
                  border: "1px solid #c8e6c9",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Comment
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {outreachDetails.comment}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* View Mode Tabs */}
        {id &&
          (companyData?.apollo_enrichment_fetched ||
            isCreatedByAgent(companyData)) && (
            <Box sx={{ mb: 1, borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={viewMode}
                onChange={(e, newValue) => setViewMode(newValue)}
                centered
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  },
                }}
              >
                <Tab label="Normal View" />
                <Tab
                  label="AI Enrichment"
                  icon={<AutoAwesome sx={{ fontSize: 16 }} />}
                  iconPosition="start"
                />
                {isCreatedByAgent(companyData) && (
                  <Tab
                    label="Pitch"
                    icon={<Email sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                  />
                )}
              </Tabs>
            </Box>
          )}

                {/* Normal View - Form */}
                {viewMode === 0 && (
                <FormikProvider value={formik}>
                    <Form
                        autoComplete="off"
                        noValidate
                        onSubmit={handleSubmit}
                        onKeyDown={(keyEvent) => {
                            if ((keyEvent.key === 'Enter')) {
                                keyEvent.preventDefault();
                            }
                        }}
                        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                    >
                        <Box sx={{ flex: 1, overflow: 'auto', mb: 1 }}>
                        <Card sx={{ mt: 1, mb: 2 }} variant="outlined">
                            <CardContent sx={{ p: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, color: '#1976d2', fontWeight: 600 }}>Basic Information</Typography>
                                
                                {/* Duplicate Warning Banner */}
                                {duplicateWarning && duplicateWarning.length > 0 && (
                                    <Box sx={{ 
                                        p: 2, 
                                        mb: 2, 
                                        backgroundColor: '#fff3e0', 
                                        borderRadius: 1, 
                                        border: '1px solid #ff9800'
                                    }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                            <ReportProblem sx={{ color: '#ff9800' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100' }}>
                                                Duplicate Company Found
                                            </Typography>
                                            {isCheckingDuplicate && <CircularProgress size={16} sx={{ ml: 1 }} />}
                                        </Stack>
                                        {duplicateWarning.map((dup, index) => (
                                            <Box key={index} sx={{ mt: 1, p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                                    {dup.message}
                                                </Typography>
                                                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Company:</strong> {dup.existing_company?.company_name}
                                                    </Typography>
                                                    {dup.existing_company?.website && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            <strong>Website:</strong> {dup.existing_company?.website}
                                                        </Typography>
                                                    )}
                                                    {dup.existing_company?.country && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            <strong>Country:</strong> {dup.existing_company?.country}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                    <Stack spacing={1}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        <Autocomplete
                          fullWidth
                          freeSolo
                          size="small"
                          options={companyNameSuggestions}
                          getOptionLabel={(option) => {
                            if (typeof option === "string") return option;
                            return option.company_name || "";
                          }}
                          inputValue={formik.values.company_name || ""}
                          onInputChange={(event, newValue) => {
                            formik.setFieldValue("company_name", newValue);
                            debouncedNameSearch(newValue);
                          }}
                          onChange={(event, value) => {
                            if (value && typeof value === "object") {
                              // User selected an existing company - set a duplicate warning
                              setDuplicateWarning([
                                {
                                  type: "name",
                                  message: `Company "${value.company_name}" already exists`,
                                  existing_company: value,
                                },
                              ]);
                              formik.setFieldValue(
                                "company_name",
                                value.company_name
                              );
                            }
                          }}
                          onBlur={() => {
                            formik.setFieldTouched("company_name", true);
                            debouncedDuplicateCheck(
                              formik.values.company_name,
                              formik.values.website
                            );
                          }}
                          loading={isLoadingNameSuggestions}
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              {...props}
                              key={option.id}
                              sx={{
                                backgroundColor: "#ffebee !important",
                                borderLeft: "3px solid #f44336",
                                "&:hover": {
                                  backgroundColor: "#ffcdd2 !important",
                                },
                              }}
                            >
                              <Stack>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#d32f2f", fontWeight: 600 }}
                                >
                                  ⚠️ {option.company_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#666" }}
                                >
                                  {option.website}{" "}
                                  {option.country ? `• ${option.country}` : ""}
                                </Typography>
                              </Stack>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Company name"
                              required
                              error={
                                Boolean(
                                  touched.company_name && errors.company_name
                                ) ||
                                duplicateWarning?.some((d) => d.type === "name")
                              }
                              helperText={
                                touched.company_name && errors.company_name
                              }
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingNameSuggestions ? (
                                      <CircularProgress size={16} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                        <Autocomplete
                          fullWidth
                          freeSolo
                          size="small"
                          options={websiteSuggestions}
                          getOptionLabel={(option) => {
                            if (typeof option === "string") return option;
                            return option.website || "";
                          }}
                          inputValue={formik.values.website || ""}
                          onInputChange={(event, newValue) => {
                            formik.setFieldValue("website", newValue);
                            debouncedWebsiteSearch(newValue);
                          }}
                          onChange={(event, value) => {
                            if (value && typeof value === "object") {
                              // User selected an existing company - set a duplicate warning
                              setDuplicateWarning([
                                {
                                  type: "website",
                                  message: `Website "${value.website}" already exists for ${value.company_name}`,
                                  existing_company: value,
                                },
                              ]);
                              formik.setFieldValue("website", value.website);
                            }
                          }}
                          onBlur={() => {
                            formik.setFieldTouched("website", true);
                            debouncedDuplicateCheck(
                              formik.values.company_name,
                              formik.values.website
                            );
                          }}
                          loading={isLoadingWebsiteSuggestions}
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              {...props}
                              key={option.id}
                              sx={{
                                backgroundColor: "#ffebee !important",
                                borderLeft: "3px solid #f44336",
                                "&:hover": {
                                  backgroundColor: "#ffcdd2 !important",
                                },
                              }}
                            >
                              <Stack>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#d32f2f", fontWeight: 600 }}
                                >
                                  ⚠️ {option.website}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#666" }}
                                >
                                  {option.company_name}{" "}
                                  {option.country ? `• ${option.country}` : ""}
                                </Typography>
                              </Stack>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Website"
                              required
                              placeholder="https://"
                              error={
                                Boolean(touched.website && errors.website) ||
                                duplicateWarning?.some(
                                  (d) => d.type === "website"
                                )
                              }
                              helperText={touched.website && errors.website}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingWebsiteSuggestions ? (
                                      <CircularProgress size={16} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Stack>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        <TextField
                          fullWidth
                          label="Email"
                          size="small"
                          {...getFieldProps("email")}
                          error={Boolean(touched.email && errors.email)}
                          helperText={touched.email && errors.email}
                        />
                        {locationData?.length > 0 ? (
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={locationData}
                            getOptionLabel={(option) => option.label || ""}
                            value={
                              locationData.find(
                                (c) => c.id === formik.values?.country
                              ) || null
                            }
                            onChange={(event, value) => {
                              formik.setFieldValue("country", value?.id || "");
                              // Update phone country code when country changes
                              if (value?.label) {
                                const isoCode = getCountryISOCode(value.label);
                                setPhoneCountryCode(isoCode);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Country"
                                required
                                error={Boolean(
                                  formik.touched?.country &&
                                    formik.errors?.country
                                )}
                                helperText={
                                  formik.touched?.country &&
                                  formik.errors?.country
                                }
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            label="Country"
                            value=""
                            disabled
                            placeholder={
                              isLocationLoading
                                ? "Loading countries..."
                                : "Country list unavailable"
                            }
                            helperText={
                              locationLoadError
                                ? "Country list failed to load. You can retry."
                                : "Country list is loading or unavailable."
                            }
                          />
                        )}
                        {businessTypeData?.length > 0 && (
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={businessTypeData}
                            getOptionLabel={(option) => option.label || ""}
                            value={
                              businessTypeData.find(
                                (b) => b.id === formik.values.business_type
                              ) || null
                            }
                            onChange={(event, value) => {
                              formik.setFieldValue(
                                "business_type",
                                value?.id || ""
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Business Type"
                                required={!id}
                                error={Boolean(
                                  formik.touched?.business_type &&
                                    formik.errors?.business_type
                                )}
                                helperText={
                                  formik.touched?.business_type &&
                                  formik.errors?.business_type
                                }
                              />
                            )}
                          />
                        )}
                      </Stack>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={offeringData}
                          getOptionLabel={(option) => option.label || ""}
                          value={
                            offeringData.find(
                              (o) => o.id === formik.values.offering
                            ) || null
                          }
                          onChange={(event, value) => {
                            formik.setFieldValue("offering", value?.id || "");
                            // Reset the dependent segment and reload its options.
                            formik.setFieldValue("segment", "");
                            getSegmentData(value?.id || "");
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Offering"
                              required={!id}
                              error={Boolean(
                                formik.touched?.offering &&
                                  formik.errors?.offering
                              )}
                              helperText={
                                formik.touched?.offering &&
                                formik.errors?.offering
                              }
                            />
                          )}
                        />
                        <Autocomplete
                          fullWidth
                          size="small"
                          disabled={!formik.values.offering}
                          options={segmentData}
                          getOptionLabel={(option) => option.label || ""}
                          value={
                            segmentData.find(
                              (s) => s.id === formik.values.segment
                            ) || null
                          }
                          onChange={(event, value) => {
                            formik.setFieldValue("segment", value?.id || "");
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Segment"
                              required={!id}
                              error={Boolean(
                                formik.touched?.segment &&
                                  formik.errors?.segment
                              )}
                              helperText={
                                formik.touched?.segment && formik.errors?.segment
                                  ? formik.errors?.segment
                                  : !formik.values.offering
                                  ? "Select an offering first"
                                  : ""
                              }
                            />
                          )}
                        />
                      </Stack>

                      {/* Partner section (C2): toggle + partner picker that
                          auto-fills BA/BM, plus editable BA/BM pickers. */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isPartnerIntroduced}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setIsPartnerIntroduced(on);
                              if (!on) {
                                formik.setFieldValue("partner", "");
                                formik.setFieldValue("ba", "");
                                formik.setFieldValue("bm", "");
                              }
                            }}
                          />
                        }
                        label="Introduced by a partner?"
                      />
                      {isPartnerIntroduced && (
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                        >
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={partnersData}
                            getOptionLabel={(o) =>
                              `${o.first_name || ""} ${o.last_name || ""}`.trim()
                            }
                            value={
                              partnersData.find(
                                (p) => p.id === formik.values.partner
                              ) || null
                            }
                            onChange={(event, value) => {
                              formik.setFieldValue("partner", value?.id || "");
                              formik.setFieldValue("ba", value?.ba || "");
                              formik.setFieldValue("bm", value?.bm || "");
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="Partner" />
                            )}
                          />
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={usersData}
                            getOptionLabel={(u) =>
                              `${u.first_name || ""} ${u.last_name || ""}`.trim()
                            }
                            value={
                              usersData.find(
                                (u) => u.pk === formik.values.ba
                              ) || null
                            }
                            onChange={(event, value) => {
                              formik.setFieldValue("ba", value?.pk || "");
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="BA" />
                            )}
                          />
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={usersData}
                            getOptionLabel={(u) =>
                              `${u.first_name || ""} ${u.last_name || ""}`.trim()
                            }
                            value={
                              usersData.find(
                                (u) => u.pk === formik.values.bm
                              ) || null
                            }
                            onChange={(event, value) => {
                              formik.setFieldValue("bm", value?.pk || "");
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="BM" />
                            )}
                          />
                        </Stack>
                      )}
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        <Stack width="100%" height="100%">
                          <PhoneInput
                            key={`phone-company-${
                              id || "new"
                            }-${phoneInputKey}`}
                            country={phoneCountryCode}
                            value={formik.values.contact_number}
                            onChange={(value) =>
                              formik.setFieldValue("contact_number", value)
                            }
                            inputProps={{
                              name: "contact_number",
                              required: true,
                            }}
                            inputStyle={{
                              width: "100%",
                              height: "2.5rem",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              fontSize: "0.875rem",
                            }}
                            buttonStyle={{
                              borderRadius: "4px 0px 0px 4px",
                              backgroundColor: "transparent",
                            }}
                          />
                          <ErrorMessage
                            name={"contact_number"}
                            component="span"
                            style={{ color: "#FF4842", fontSize: "12px" }}
                          />
                        </Stack>
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: 2, mb: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#666", fontWeight: 600 }}
                        >
                          Business Description
                        </Typography>
                      </Stack>

                      <TextEditor
                        name="Business Description"
                        placeholder="Detailed business description..."
                        minHeight={120}
                        value={values.business_description}
                        onChange={(value) =>
                          setFieldValue("business_description", value)
                        }
                      />
                      <ErrorMessage name="business_description">
                        {(msg) => (
                          <span style={{ color: "#FF4842", fontSize: "12px" }}>
                            {msg}
                          </span>
                        )}
                      </ErrorMessage>
                    </Stack>
                  </CardContent>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, color: "#1976d2", fontWeight: 600 }}
                    >
                      Company Members
                    </Typography>

                    {touched.company_members &&
                      typeof errors.company_members === "string" && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mb: 1 }}
                        >
                          {errors.company_members}
                        </Typography>
                      )}

                    {/* List of Added Members */}
                    {remainingMembers?.length > 0 ? (
                      <Card sx={{ mb: 2 }}>
                        <CardContent sx={{ p: 1 }}>
                          {formik.values.company_members?.map(
                            (member, originalIndex) => {
                              // Skip deleted members
                              if (member.status === "DELETED") return null;

                              const memberErrors =
                                errors.company_members?.[originalIndex] || {};
                              const hasErrors =
                                Object.keys(memberErrors).length > 0;

                              return (
                                <Stack
                                  key={member.id || originalIndex}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={1}
                                >
                                  <Stack
                                    direction="column"
                                    spacing={1}
                                    sx={{ width: "100%" }}
                                  >
                                    <Typography
                                      variant={matches ? "body2" : "body1"}
                                    >
                                      {member.first_name} {member.last_name} |{" "}
                                      {member.email}
                                    </Typography>
                                    {hasErrors &&
                                      Object.keys(memberErrors).map((field) => (
                                        <Typography
                                          key={field}
                                          variant="body2"
                                          color="error"
                                        >
                                          {String(memberErrors[field])}
                                        </Typography>
                                      ))}
                                  </Stack>

                                  <Stack direction="row" spacing={1}>
                                    <IconButton
                                      onClick={() =>
                                        handleEditClick(originalIndex)
                                      }
                                    >
                                      <Edit />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        handleDeleteClick(originalIndex)
                                      }
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                              );
                            }
                          )}
                        </CardContent>
                      </Card>
                    ) : null}

                    <FormikProvider value={companyMemberFormik}>
                      <Box
                        onKeyDown={(keyEvent) => {
                          if (keyEvent.key === "Enter") {
                            keyEvent.preventDefault();
                          }
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                          >
                            <TextField
                              variant="standard"
                              fullWidth
                              label="First Name"
                              required
                              {...companyMemberFormik.getFieldProps(
                                "first_name"
                              )}
                              onChange={handleNameChange("first_name")}
                              error={Boolean(
                                companyMemberFormik.touched?.first_name &&
                                  companyMemberFormik.errors?.first_name
                              )}
                              helperText={
                                companyMemberFormik.touched?.first_name &&
                                companyMemberFormik.errors?.first_name
                              }
                            />
                            <TextField
                              variant="standard"
                              fullWidth
                              label="Last Name"
                              required
                              {...companyMemberFormik.getFieldProps(
                                "last_name"
                              )}
                              onChange={handleNameChange("last_name")}
                              error={Boolean(
                                companyMemberFormik.touched?.last_name &&
                                  companyMemberFormik.errors?.last_name
                              )}
                              helperText={
                                companyMemberFormik.touched?.last_name &&
                                companyMemberFormik.errors?.last_name
                              }
                            />
                          </Stack>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                          >
                            <TextField
                              variant="standard"
                              fullWidth
                              label="Email"
                              required
                              {...companyMemberFormik.getFieldProps("email")}
                              error={Boolean(
                                companyMemberFormik.touched?.email &&
                                  companyMemberFormik.errors?.email
                              )}
                              helperText={
                                companyMemberFormik.touched?.email &&
                                companyMemberFormik.errors?.email
                              }
                            />
                            {locationData?.length > 0 && (
                              <Autocomplete
                                fullWidth
                                size="small"
                                options={locationData}
                                getOptionLabel={(option) => option.label || ""}
                                value={
                                  locationData.find(
                                    (c) =>
                                      c.id ===
                                      companyMemberFormik.values?.country
                                  ) || null
                                }
                                onChange={(event, value) => {
                                  companyMemberFormik.setFieldValue(
                                    "country",
                                    value?.id || ""
                                  );
                                  // Update member phone country code when country changes
                                  if (value?.label) {
                                    const isoCode = getCountryISOCode(
                                      value.label
                                    );
                                    setMemberPhoneCountryCode(isoCode);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    variant="standard"
                                    {...params}
                                    label="Country"
                                    error={Boolean(
                                      companyMemberFormik.touched?.country &&
                                        companyMemberFormik.errors?.country
                                    )}
                                    helperText={
                                      companyMemberFormik.touched?.country &&
                                      companyMemberFormik.errors?.country
                                    }
                                  />
                                )}
                              />
                            )}
                          </Stack>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                          >
                            <Stack sx={{ width: { xs: "100%", sm: "50%" } }}>
                              <PhoneInput
                                key={memberPhoneInputKey}
                                country={memberPhoneCountryCode}
                                value={companyMemberFormik.values.mobile}
                                onChange={(value) =>
                                  companyMemberFormik.setFieldValue(
                                    "mobile",
                                    value
                                  )
                                }
                                inputProps={{
                                  name: "mobile",
                                }}
                                inputStyle={{
                                  width: "100%",
                                  height: "3rem",
                                  border: "none",
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                                  borderRadius: "0px",
                                  paddingBottom: "4px",
                                }}
                                buttonStyle={{
                                  border: "none",
                                  backgroundColor: "transparent",
                                }}
                                containerStyle={{ width: "100%" }}
                              />
                              <ErrorMessage
                                name={"mobile"}
                                component="span"
                                style={{ color: "#FF4842", fontSize: "14px" }}
                              />
                            </Stack>
                            <TextField
                              variant="standard"
                              sx={{ width: { xs: "100%", sm: "50%" } }}
                              label="LinkedIn Profile"
                              {...companyMemberFormik.getFieldProps(
                                "linkedin_profile"
                              )}
                              error={Boolean(
                                companyMemberFormik.touched?.linkedin_profile &&
                                  companyMemberFormik.errors?.linkedin_profile
                              )}
                              helperText={
                                companyMemberFormik.touched?.linkedin_profile &&
                                companyMemberFormik.errors?.linkedin_profile
                              }
                            />
                          </Stack>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                          >
                            {cmds.length > 0 ? (
                              <Autocomplete
                                fullWidth
                                id="cmd"
                                multiple
                                options={cmds}
                                getOptionLabel={(option) => option.label}
                                value={cmds.filter((c) =>
                                  companyMemberFormik.values?.cmd?.includes(
                                    c.id
                                  )
                                )}
                                onChange={(event, value) => {
                                  const selectedIds = value.map(
                                    (item) => item.id
                                  );
                                  companyMemberFormik.setFieldValue(
                                    "cmd",
                                    selectedIds
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    variant="standard"
                                    {...params}
                                    label="Designation"
                                    error={Boolean(
                                      companyMemberFormik.touched.cmd &&
                                        companyMemberFormik.errors.cmd
                                    )}
                                    helperText={
                                      companyMemberFormik.touched.cmd &&
                                      companyMemberFormik.errors.cmd
                                    }
                                  />
                                )}
                              ></Autocomplete>
                            ) : null}
                          </Stack>

                          <Stack
                            direction="row"
                            justifyContent="center"
                            spacing={1}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="inherit"
                              onClick={companyMemberFormik.handleSubmit}
                            >
                              {editIndex !== null ? "Update" : "Add Member"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              onClick={handleCompanyMemberReset}
                            >
                              Clear
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    </FormikProvider>
                  </CardContent>
                </Card>
              </Box>

              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "white",
                  borderTop: "1px solid #e0e0e0",
                  p: 1,
                  zIndex: 1000,
                }}
              >
                <Card variant="outlined">
                  <CardContent sx={{ p: 1 }}>
                    <LoadingButton
                      size="medium"
                      type="submit"
                      variant="contained"
                      loading={formik.isSubmitting}
                      disabled={formik.isSubmitting}
                      style={{ display: "block", margin: "auto", width: 200 }}
                    >
                      {id ? "Update Company" : "Add Company"}
                    </LoadingButton>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                    ></Stack>
                  </CardContent>
                </Card>
              </Box>
            </Form>
          </FormikProvider>
        )}

        {/* AI Enrichment View */}
        {viewMode === 1 &&
          companyData?.apollo_enrichment_data?.organization && (
            <Box sx={{ flex: 1, overflow: "auto", pb: 3 }}>
              {(() => {
                const org = companyData.apollo_enrichment_data.organization;

                const people = companyData.apollo_enrichment_data.people;

                const perplexity_enrichment_data =
                  companyData.perplexity_enrichment_data;

                return (
                  <Box>
                    {/* Company Header: Logo & Description */}
                    {(org.logo_url || org.short_description) && (
                      <Card variant="outlined" sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 3,
                          }}
                        >
                          {org.logo_url && (
                            <Box
                              sx={{
                                width: { xs: "100%", sm: "180px" },
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fafafa",
                                borderRadius: 1,
                                p: 2,
                                border: "1px solid #f0f0f0",
                              }}
                            >
                              <img
                                src={org.logo_url}
                                alt={org.name}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "80px",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>
                          )}

                          <Box sx={{ flex: 1 }}>
                            {org.name && (
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 1,
                                  fontWeight: 700,
                                  color: "#212B36",
                                }}
                              >
                                {org.name}
                              </Typography>
                            )}
                            {org.short_description && (
                              <ExpandableText
                                text={org.short_description}
                                limit={250}
                              />
                            )}
                          </Box>
                        </Box>
                      </Card>
                    )}

                    {/* Perplexity Data Section */}
                    <Box sx={{ mb: 3 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 2 }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "#1976d2",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontWeight: 700,
                          }}
                        >
                          <AutoAwesome fontSize="small" />
                          Deep Intelligence
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={
                            isPerplexityEnriching ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Refresh />
                            )
                          }
                          disabled={
                            isPerplexityEnriching ||
                            (perplexity_enrichment_data &&
                              Object.keys(perplexity_enrichment_data).length >
                                0)
                          }
                          onClick={async () => {
                            setIsPerplexityEnriching(true);
                            try {
                              await apiInstance.post(
                                `company/${id}/enrich-perplexity/`
                              );
                              showToast(
                                "Deep Intelligence enrichment triggered. Data will appear in ~10 seconds."
                              );
                              // Auto-refresh after 10 seconds
                              setTimeout(() => {
                                getCompanydata();
                                setIsPerplexityEnriching(false);
                              }, 10000);
                            } catch (error) {
                              showErrorToast(error);
                              setIsPerplexityEnriching(false);
                            }
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          {isPerplexityEnriching
                            ? "Enriching..."
                            : "Retry Enrichment"}
                        </Button>
                      </Stack>

                      {perplexity_enrichment_data &&
                      Object.keys(perplexity_enrichment_data).length > 0 ? (
                        <>
                          <Grid container spacing={2}>
                            {/* Key Value Props & Business Model */}
                            {(perplexity_enrichment_data.core_value_proposition ||
                              perplexity_enrichment_data.business_model) && (
                              <Grid item xs={12}>
                                <Card
                                  variant="outlined"
                                  sx={{ height: "100%" }}
                                >
                                  <CardContent sx={{ p: 2.5 }}>
                                    <Grid container spacing={3}>
                                      {perplexity_enrichment_data.core_value_proposition && (
                                        <Grid
                                          item
                                          xs={12}
                                          md={
                                            perplexity_enrichment_data.business_model
                                              ? 6
                                              : 12
                                          }
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, color: "#637381" }}
                                          >
                                            Core Value Proposition
                                          </Typography>
                                          <ExpandableText
                                            text={
                                              perplexity_enrichment_data.core_value_proposition
                                            }
                                            limit={200}
                                          />
                                        </Grid>
                                      )}
                                      {perplexity_enrichment_data.business_model && (
                                        <Grid
                                          item
                                          xs={12}
                                          md={
                                            perplexity_enrichment_data.core_value_proposition
                                              ? 6
                                              : 12
                                          }
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, color: "#637381" }}
                                          >
                                            Business Model
                                          </Typography>
                                          <ExpandableText
                                            text={
                                              perplexity_enrichment_data.business_model
                                            }
                                            limit={200}
                                          />
                                        </Grid>
                                      )}
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>
                            )}

                            {/* Market Position & Advantages */}
                            {(perplexity_enrichment_data.market_position ||
                              perplexity_enrichment_data.headquarters_location ||
                              perplexity_enrichment_data.competitive_advantages
                                ?.length > 0) && (
                              <Grid item xs={12}>
                                <Card variant="outlined">
                                  <CardContent sx={{ p: 2.5 }}>
                                    <Stack spacing={2}>
                                      {/* Locations */}
                                      {(perplexity_enrichment_data.headquarters_location ||
                                        perplexity_enrichment_data.other_offices
                                          ?.length > 0) && (
                                        <Box>
                                          <Typography
                                            variant="subtitle2"
                                            sx={{
                                              mb: 1,
                                              color: "#637381",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            <LocationOn fontSize="small" />{" "}
                                            Headquarters & Offices
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                          >
                                            {perplexity_enrichment_data.headquarters_location ||
                                              "N/A"}
                                          </Typography>
                                          {perplexity_enrichment_data
                                            .other_offices?.length > 0 && (
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              sx={{ mt: 0.5 }}
                                            >
                                              Other:{" "}
                                              {perplexity_enrichment_data.other_offices.join(
                                                ", "
                                              )}
                                            </Typography>
                                          )}
                                        </Box>
                                      )}

                                      {perplexity_enrichment_data.market_position && (
                                        <Box>
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, color: "#637381" }}
                                          >
                                            Market Position
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{ lineHeight: 1.6 }}
                                          >
                                            {
                                              perplexity_enrichment_data.market_position
                                            }
                                          </Typography>
                                        </Box>
                                      )}
                                      {perplexity_enrichment_data
                                        .competitive_advantages?.length > 0 && (
                                        <Box>
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, color: "#637381" }}
                                          >
                                            Competitive Advantages
                                          </Typography>
                                          <Stack
                                            direction="row"
                                            flexWrap="wrap"
                                            gap={1}
                                          >
                                            {perplexity_enrichment_data.competitive_advantages.map(
                                              (adv, idx) => (
                                                <Chip
                                                  key={idx}
                                                  label={adv}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: "#e3f2fd",
                                                    color: "#1565c0",
                                                    borderRadius: 1,
                                                  }}
                                                />
                                              )
                                            )}
                                          </Stack>
                                        </Box>
                                      )}
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>
                            )}

                            {/* Financial & Stats - 4 Card Blocks */}
                            <Grid item xs={12}>
                              <Grid container spacing={2}>
                                {/* Revenue & Funding Card */}
                                {(perplexity_enrichment_data.current_revenue ||
                                  perplexity_enrichment_data.total_funding_raised) && (
                                  <Grid item xs={12} md={6}>
                                    <Card
                                      variant="outlined"
                                      sx={{ height: "100%" }}
                                    >
                                      <CardContent sx={{ p: 2.5 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            mb: 2,
                                            color: "#00AB55",
                                            fontWeight: 600,
                                          }}
                                        >
                                          💰 Financial Overview
                                        </Typography>
                                        <Stack spacing={2}>
                                          {perplexity_enrichment_data.current_revenue && (
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "#637381",
                                                  display: "block",
                                                  mb: 0.5,
                                                }}
                                              >
                                                Revenue
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ lineHeight: 1.6 }}
                                              >
                                                {
                                                  perplexity_enrichment_data.current_revenue
                                                }
                                              </Typography>
                                            </Box>
                                          )}
                                          {perplexity_enrichment_data.total_funding_raised && (
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "#637381",
                                                  display: "block",
                                                  mb: 0.5,
                                                }}
                                              >
                                                Funding Raised
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ lineHeight: 1.6 }}
                                              >
                                                {
                                                  perplexity_enrichment_data.total_funding_raised
                                                }
                                              </Typography>
                                            </Box>
                                          )}
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )}

                                {/* Growth & Employees Card */}
                                {(perplexity_enrichment_data.number_of_employees ||
                                  perplexity_enrichment_data.yearly_growth_rate) && (
                                  <Grid item xs={12} md={6}>
                                    <Card
                                      variant="outlined"
                                      sx={{ height: "100%" }}
                                    >
                                      <CardContent sx={{ p: 2.5 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            mb: 2,
                                            color: "#1976d2",
                                            fontWeight: 600,
                                          }}
                                        >
                                          📈 Growth & Team
                                        </Typography>
                                        <Stack spacing={2}>
                                          {perplexity_enrichment_data.number_of_employees && (
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "#637381",
                                                  display: "block",
                                                  mb: 0.5,
                                                }}
                                              >
                                                Employees
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ lineHeight: 1.6 }}
                                              >
                                                {
                                                  perplexity_enrichment_data.number_of_employees
                                                }
                                              </Typography>
                                            </Box>
                                          )}
                                          {perplexity_enrichment_data.yearly_growth_rate && (
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "#637381",
                                                  display: "block",
                                                  mb: 0.5,
                                                }}
                                              >
                                                Growth Rate
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ lineHeight: 1.6 }}
                                              >
                                                {
                                                  perplexity_enrichment_data.yearly_growth_rate
                                                }
                                              </Typography>
                                            </Box>
                                          )}
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )}

                                {/* Profit/Loss Card */}
                                {perplexity_enrichment_data.profit_or_loss && (
                                  <Grid item xs={12} md={6}>
                                    <Card
                                      variant="outlined"
                                      sx={{ height: "100%" }}
                                    >
                                      <CardContent sx={{ p: 2.5 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            mb: 2,
                                            color: "#ed6c02",
                                            fontWeight: 600,
                                          }}
                                        >
                                          📊 Profitability
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ lineHeight: 1.6 }}
                                        >
                                          {
                                            perplexity_enrichment_data.profit_or_loss
                                          }
                                        </Typography>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )}

                                {/* Equity Card */}
                                {perplexity_enrichment_data.equity && (
                                  <Grid item xs={12} md={6}>
                                    <Card
                                      variant="outlined"
                                      sx={{ height: "100%" }}
                                    >
                                      <CardContent sx={{ p: 2.5 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            mb: 2,
                                            color: "#9c27b0",
                                            fontWeight: 600,
                                          }}
                                        >
                                          🏦 Equity & Ownership
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ lineHeight: 1.6 }}
                                        >
                                          {perplexity_enrichment_data.equity}
                                        </Typography>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>

                            {/* Risk Factors */}
                            {perplexity_enrichment_data.risk_factors?.length >
                              0 && (
                              <Grid item xs={12} md={6}>
                                <Card
                                  variant="outlined"
                                  sx={{ height: "100%" }}
                                >
                                  <CardContent sx={{ p: 2.5 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        mb: 2,
                                        color: "#d32f2f",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <ReportProblem fontSize="small" /> Risk
                                      Factors
                                    </Typography>
                                    <Stack spacing={1}>
                                      {perplexity_enrichment_data.risk_factors.map(
                                        (risk, idx) => (
                                          <Typography
                                            key={idx}
                                            variant="body2"
                                            component="div"
                                            sx={{
                                              display: "flex",
                                              gap: 1.5,
                                              lineHeight: 1.5,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                minWidth: 4,
                                                height: 4,
                                                borderRadius: "50%",
                                                bgcolor: "#d32f2f",
                                                mt: 1,
                                              }}
                                            />
                                            {risk}
                                          </Typography>
                                        )
                                      )}
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>
                            )}

                            {/* Key Investors & Customers */}
                            {(perplexity_enrichment_data.key_investors?.length >
                              0 ||
                              perplexity_enrichment_data.main_customers
                                ?.length > 0) && (
                              <Grid item xs={12} md={6}>
                                <Card
                                  variant="outlined"
                                  sx={{ height: "100%" }}
                                >
                                  <CardContent sx={{ p: 2.5 }}>
                                    {perplexity_enrichment_data.key_investors
                                      ?.length > 0 && (
                                      <Box sx={{ mb: 3 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ mb: 1.5, color: "#637381" }}
                                        >
                                          Key Investors
                                        </Typography>
                                        <Stack
                                          direction="row"
                                          flexWrap="wrap"
                                          gap={1}
                                        >
                                          {perplexity_enrichment_data.key_investors.map(
                                            (investor, idx) => (
                                              <Chip
                                                key={idx}
                                                label={investor}
                                                size="small"
                                                variant="outlined"
                                              />
                                            )
                                          )}
                                        </Stack>
                                      </Box>
                                    )}
                                    {perplexity_enrichment_data.main_customers
                                      ?.length > 0 && (
                                      <Box>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ mb: 1.5, color: "#637381" }}
                                        >
                                          Main Customers
                                        </Typography>
                                        <Stack
                                          direction="row"
                                          flexWrap="wrap"
                                          gap={1}
                                        >
                                          {perplexity_enrichment_data.main_customers.map(
                                            (customer, idx) => (
                                              <Chip
                                                key={idx}
                                                label={customer}
                                                size="small"
                                                variant="outlined"
                                              />
                                            )
                                          )}
                                        </Stack>
                                      </Box>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            )}

                            {/* Funding Rounds Table */}
                            {perplexity_enrichment_data.funding_rounds?.length >
                              0 && (
                              <Grid item xs={12}>
                                <Card variant="outlined">
                                  <CardContent sx={{ p: 0 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ p: 2.5, pb: 1, color: "#637381" }}
                                    >
                                      Detailed Funding Rounds
                                    </Typography>
                                    <TableContainer>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: "#f9fafb" }}>
                                            <TableCell
                                              sx={{
                                                color: "#637381",
                                                fontWeight: 600,
                                              }}
                                            >
                                              Date
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                color: "#637381",
                                                fontWeight: 600,
                                              }}
                                            >
                                              Round
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                color: "#637381",
                                                fontWeight: 600,
                                              }}
                                            >
                                              Amount
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                color: "#637381",
                                                fontWeight: 600,
                                              }}
                                            >
                                              Investors
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {perplexity_enrichment_data.funding_rounds.map(
                                            (round, idx) => (
                                              <TableRow key={idx} hover>
                                                <TableCell>
                                                  {round.date || "-"}
                                                </TableCell>
                                                <TableCell>
                                                  {round.round_type && (
                                                    <Chip
                                                      label={round.round_type}
                                                      size="small"
                                                      color="primary"
                                                      variant="outlined"
                                                      sx={{ height: 24 }}
                                                    />
                                                  )}
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontWeight: 500 }}
                                                >
                                                  {round.amount || "-"}
                                                </TableCell>
                                                <TableCell>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: "text.secondary",
                                                    }}
                                                  >
                                                    {Array.isArray(
                                                      round.investors
                                                    )
                                                      ? round.investors.join(
                                                          ", "
                                                        )
                                                      : round.investors || "-"}
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            )
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </CardContent>
                                </Card>
                              </Grid>
                            )}
                          </Grid>
                        </>
                      ) : (
                        <Card
                          variant="outlined"
                          sx={{ p: 3, textAlign: "center", bgcolor: "#f9f9f9" }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No Deep Intelligence data available. Click "Retry
                            Enrichment" to fetch data.
                          </Typography>
                        </Card>
                      )}
                    </Box>

                    {/* Single Company Information Table */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
                      >
                        Company Information
                      </Typography>
                      <Card variant="outlined">
                        <Box
                          component="table"
                          sx={{ width: "100%", borderCollapse: "collapse" }}
                        >
                          <Box component="tbody">
                            {/* Website */}
                            {org.website_url && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                    verticalAlign: "top",
                                  }}
                                >
                                  Website
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#1976d2",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                      wordBreak: "break-word",
                                      "&:hover": { opacity: 0.8 },
                                    }}
                                    onClick={() =>
                                      window.open(org.website_url, "_blank")
                                    }
                                  >
                                    {org.website_url}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Industries */}
                            {org.industries && org.industries.length > 0 && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Industries
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.industries.join(", ")}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Founded Year */}
                            {org.founded_year && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Founded Year
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.founded_year}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Employees */}
                            {org.estimated_num_employees && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Employees
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.estimated_num_employees.toLocaleString()}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Phone */}
                            {org.phone && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Phone
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.phone}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Primary Phone */}
                            {org.primary_phone?.number && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Primary Phone
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.primary_phone.number}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Email */}
                            {org.email && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Email
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.email}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Country */}
                            {org.country && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Country
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.country}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* LinkedIn */}
                            {org.linkedin_url && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  LinkedIn
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#1976d2",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                      "&:hover": { opacity: 0.8 },
                                    }}
                                    onClick={() =>
                                      window.open(org.linkedin_url, "_blank")
                                    }
                                  >
                                    View Profile
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Annual Revenue */}
                            {(org.annual_revenue_printed ||
                              org.annual_revenue) && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Annual Revenue
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.annual_revenue_printed ||
                                      `$${org.annual_revenue.toLocaleString()}`}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Total Funding */}
                            {org.total_funding_printed && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Total Funding
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.total_funding_printed}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Latest Funding Stage */}
                            {org.latest_funding_stage && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Latest Funding Stage
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.latest_funding_stage}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Latest Funding Date */}
                            {org.latest_funding_round_date && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Latest Funding Date
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    {org.latest_funding_round_date}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Total Funding Amount */}
                            {org.total_funding && (
                              <Box
                                component="tr"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    p: 1.5,
                                    fontWeight: 600,
                                    color: "#666",
                                    width: "30%",
                                  }}
                                >
                                  Total Funding Amount
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                  <Typography variant="body2">
                                    ${org.total_funding.toLocaleString()}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    </Box>

                    {/* Company Members */}
                    {people && people.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <EnrichmentSectionHeader
                          title="Team Members"
                          icon={People}
                          section="people"
                          count={people.length}
                        />
                        {expandedSections.people && (
                          <Card variant="outlined">
                            <Box
                              component="table"
                              sx={{ width: "100%", borderCollapse: "collapse" }}
                            >
                              <Box component="thead">
                                <Box
                                  component="tr"
                                  sx={{ backgroundColor: "#fafafa" }}
                                >
                                  <Box
                                    component="th"
                                    sx={{
                                      p: 1.5,
                                      textAlign: "left",
                                      fontWeight: 600,
                                      color: "#666",
                                      borderBottom: "2px solid #e0e0e0",
                                    }}
                                  >
                                    Name
                                  </Box>
                                  <Box
                                    component="th"
                                    sx={{
                                      p: 1.5,
                                      textAlign: "left",
                                      fontWeight: 600,
                                      color: "#666",
                                      borderBottom: "2px solid #e0e0e0",
                                    }}
                                  >
                                    Title
                                  </Box>
                                  <Box
                                    component="th"
                                    sx={{
                                      p: 1.5,
                                      textAlign: "left",
                                      fontWeight: 600,
                                      color: "#666",
                                      borderBottom: "2px solid #e0e0e0",
                                    }}
                                  >
                                    Email
                                  </Box>
                                  <Box
                                    component="th"
                                    sx={{
                                      p: 1.5,
                                      textAlign: "left",
                                      fontWeight: 600,
                                      color: "#666",
                                      borderBottom: "2px solid #e0e0e0",
                                    }}
                                  >
                                    LinkedIn
                                  </Box>
                                  <Box
                                    component="th"
                                    sx={{
                                      p: 1.5,
                                      textAlign: "left",
                                      fontWeight: 600,
                                      color: "#666",
                                      borderBottom: "2px solid #e0e0e0",
                                    }}
                                  >
                                    Add Member
                                  </Box>
                                </Box>
                              </Box>
                              <Box component="tbody">
                                {people
                                  .slice(0, peopleDisplayCount)
                                  .map((person, idx) => (
                                    <Box
                                      component="tr"
                                      key={person.apollo_id || `person-${idx}`}
                                      sx={{
                                        borderBottom: "1px solid #e0e0e0",
                                        "&:hover": {
                                          backgroundColor: "#f8f9fa",
                                        },
                                      }}
                                    >
                                      <Box
                                        component="td"
                                        sx={{ p: 1.5, verticalAlign: "middle" }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                          }}
                                        >
                                          <Avatar
                                            src={person.photo_url}
                                            alt={`${person.first_name} ${person.last_name}`}
                                            sx={{
                                              width: 40,
                                              height: 40,
                                              fontSize: "0.875rem",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {person.first_name?.[0]}
                                            {person.last_name?.[0]}
                                          </Avatar>
                                          <Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                lineHeight: 1.4,
                                              }}
                                            >
                                              {person.first_name}{" "}
                                              {person.last_name}
                                            </Typography>
                                            {person.email_status ===
                                              "verified" && (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                  mt: 0.25,
                                                }}
                                              >
                                                <VerifiedUser
                                                  sx={{
                                                    fontSize: 12,
                                                    color: "#4caf50",
                                                  }}
                                                />
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: "#4caf50",
                                                    fontSize: "0.7rem",
                                                  }}
                                                >
                                                  Verified
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        </Box>
                                      </Box>
                                      <Box
                                        component="td"
                                        sx={{ p: 1.5, verticalAlign: "middle" }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#666" }}
                                        >
                                          {person.title || "-"}
                                        </Typography>
                                      </Box>
                                      <Box
                                        component="td"
                                        sx={{ p: 1.5, verticalAlign: "middle" }}
                                      >
                                        {person.email && (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: person.email.includes(
                                                "email_not_unlocked"
                                              )
                                                ? "#999"
                                                : "#333",
                                              fontStyle: person.email.includes(
                                                "email_not_unlocked"
                                              )
                                                ? "italic"
                                                : "normal",
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {person.email.includes(
                                              "email_not_unlocked"
                                            )
                                              ? "Not available"
                                              : person.email}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box
                                        component="td"
                                        sx={{ p: 1.5, verticalAlign: "middle" }}
                                      >
                                        {person.linkedin_url ? (
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              window.open(
                                                person.linkedin_url,
                                                "_blank"
                                              )
                                            }
                                            sx={{
                                              color: "#0077b5",
                                              "&:hover": {
                                                backgroundColor:
                                                  "rgba(0, 119, 181, 0.08)",
                                              },
                                            }}
                                          >
                                            <LinkedIn fontSize="small" />
                                          </IconButton>
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            sx={{ color: "#999" }}
                                          >
                                            -
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box
                                        component="td"
                                        sx={{ p: 1.5, verticalAlign: "middle" }}
                                      >
                                        {(() => {
                                          // Check if member already exists in company_members (from API or added in session)
                                          const isAlreadyMember =
                                            formik.values.company_members?.some(
                                              (m) =>
                                                m.email?.toLowerCase() ===
                                                  person.email?.toLowerCase() &&
                                                m.status !== "DELETED"
                                            );
                                          return (
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              justifyContent="center"
                                            >
                                              <Button
                                                variant="contained"
                                                size="small"
                                                color={
                                                  isAlreadyMember
                                                    ? "success"
                                                    : "primary"
                                                }
                                                disabled={
                                                  !person.email ||
                                                  person.email_status !==
                                                    "verified" ||
                                                  isAlreadyMember ||
                                                  addingMemberEmail ===
                                                    person.email
                                                }
                                                onClick={() =>
                                                  handleAddApolloMember(person)
                                                }
                                              >
                                                {addingMemberEmail ===
                                                person.email
                                                  ? "Adding..."
                                                  : isAlreadyMember
                                                  ? "Added"
                                                  : "Add"}
                                              </Button>
                                            </Stack>
                                          );
                                        })()}
                                      </Box>
                                    </Box>
                                  ))}
                              </Box>
                            </Box>

                            {/* Load More / Show Less */}
                            {people.length > peopleDisplayCount && (
                              <Box
                                sx={{
                                  p: 2,
                                  textAlign: "center",
                                  borderTop: "1px solid #e0e0e0",
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                <Button
                                  size="small"
                                  onClick={() =>
                                    setPeopleDisplayCount((prev) => prev + 10)
                                  }
                                  endIcon={<KeyboardArrowDown />}
                                  sx={{ textTransform: "none" }}
                                >
                                  Show More (
                                  {people.length - peopleDisplayCount}{" "}
                                  remaining)
                                </Button>
                              </Box>
                            )}
                            {peopleDisplayCount > 10 &&
                              peopleDisplayCount >= people.length && (
                                <Box
                                  sx={{
                                    p: 2,
                                    textAlign: "center",
                                    borderTop: "1px solid #e0e0e0",
                                    backgroundColor: "#fafafa",
                                  }}
                                >
                                  <Button
                                    size="small"
                                    onClick={() => setPeopleDisplayCount(10)}
                                    endIcon={<KeyboardArrowUp />}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Show Less
                                  </Button>
                                </Box>
                              )}
                          </Card>
                        )}
                      </Box>
                    )}

                    {/* Funding Events */}
                    {org.funding_events && org.funding_events.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <EnrichmentSectionHeader
                          title="Funding History"
                          icon={Language}
                          section="funding"
                          count={org.funding_events.length}
                        />
                        {expandedSections.funding && (
                          <Card variant="outlined" sx={{ p: 2 }}>
                            {org.funding_events.map((event, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  p: 1.5,
                                  mb: 1.5,
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: 1,
                                  borderLeft: "3px solid #1976d2",
                                }}
                              >
                                <Grid container spacing={1}>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Type
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {event.type || "N/A"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Date
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {event.date || "N/A"}
                                    </Typography>
                                  </Grid>
                                  {event.amount && (
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Amount
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: "#2e7d32",
                                        }}
                                      >
                                        {event.currency || "$"}
                                        {event.amount}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {event.investors && (
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Investors
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {event.investors}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            ))}
                          </Card>
                        )}
                      </Box>
                    )}

                    {/* Suborganizations */}
                    {org.suborganizations &&
                      org.suborganizations.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <EnrichmentSectionHeader
                            title="Subsidiaries"
                            icon={Business}
                            section="suborganizations"
                            count={org.suborganizations.length}
                          />
                          {expandedSections.suborganizations && (
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Grid container spacing={1}>
                                {org.suborganizations
                                  .slice(0, 15)
                                  .map((sub, idx) => (
                                    <Grid item xs={12} sm={6} key={idx}>
                                      <Box
                                        sx={{
                                          p: 1.5,
                                          backgroundColor: "#f8f9fa",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                        >
                                          {sub.name}
                                        </Typography>
                                        {sub.website_url && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "#1976d2",
                                              cursor: "pointer",
                                              textDecoration: "underline",
                                              "&:hover": { opacity: 0.8 },
                                            }}
                                            onClick={() =>
                                              window.open(
                                                sub.website_url,
                                                "_blank"
                                              )
                                            }
                                          >
                                            Visit Website
                                          </Typography>
                                        )}
                                      </Box>
                                    </Grid>
                                  ))}
                                {org.suborganizations.length > 15 && (
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ textAlign: "center" }}
                                    >
                                      + {org.suborganizations.length - 15} more
                                      subsidiaries
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Card>
                          )}
                        </Box>
                      )}
                  </Box>
                );
              })()}
            </Box>
          )}

        {/* Pitch View */}
        {viewMode === 2 && isCreatedByAgent(companyData) && (
          <Box sx={{ flex: 1, overflow: "auto", pb: 3, pt: 1 }}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" color="primary" mb={2}>
                  AI Outreach Pitch
                </Typography>
                {/* Pitch Text Area */}
                <TextEditor
                  name="AI Outreach Pitch"
                  placeholder="Enter or edit the pitch here..."
                  minHeight={150}
                  value={pitchText}
                  onChange={(value) => setPitchText(value)}
                  allowHtmlMode={true}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  mt={2}
                  justifyContent="flex-end"
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsPreviewDialogOpen(true)}
                    disabled={!pitchText}
                  >
                    Preview
                  </Button>
                  <LoadingButton
                    variant="contained"
                    size="small"
                    startIcon={<Save />}
                    onClick={handlePitchSave}
                    loading={isSavingPitch}
                  >
                    Save Pitch
                  </LoadingButton>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary">
                    Send to Company Members
                  </Typography>

                  {/* From Dropdown */}
                  <Autocomplete
                    fullWidth
                    options={
                      senderDetails?.filter((item) => item.is_verified) || []
                    }
                    getOptionLabel={(option) =>
                      option.sender_name || option.email
                    }
                    value={
                      senderDetails?.find((s) => s.id === selectedSender) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      setSelectedSender(newValue?.id || "");
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.sender_name || option.email}
                          </Typography>
                          {option.sender_name && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {option.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="From *"
                        size="small"
                        helperText={
                          senderDetails?.filter((s) => s.is_verified).length ===
                          0
                            ? "No verified senders available"
                            : ""
                        }
                      />
                    )}
                    noOptionsText="No verified senders available"
                  />

                  <Autocomplete
                    multiple
                    fullWidth
                    loading={isLoadingCompanyData}
                    options={(companyData?.company_members || []).filter(
                      (m) => m.id
                    )}
                    getOptionLabel={(option) =>
                      `${option.first_name} ${option.last_name}`
                    }
                    value={
                      (companyData?.company_members || []).filter((m) =>
                        selectedMemberIds.includes(m.id)
                      ) || []
                    }
                    onChange={(event, newValue) => {
                      setSelectedMemberIds(newValue.map((m) => m.id));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.first_name} {option.last_name}
                          </Typography>
                          {option.email && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {option.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Members *"
                        placeholder={
                          isLoadingCompanyData
                            ? "Loading members..."
                            : "Choose members to send pitch"
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingCompanyData ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={
                      isLoadingCompanyData
                        ? "Loading..."
                        : "No company members found. Add members in the Normal View tab."
                    }
                  />

                  <LoadingButton
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={<Send />}
                    onClick={handleSendPitch}
                    loading={isSendingPitch}
                    disabled={selectedMemberIds.length === 0 || !selectedSender}
                  >
                    Send Pitch ({selectedMemberIds.length})
                  </LoadingButton>
                </Stack>
              </CardContent>
            </Card>

            {/* Pitch Delivery History */}
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  Pitch Delivery History
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell>Sent At</TableCell>
                        <TableCell>Sender</TableCell>
                        <TableCell>Recipients</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pitchHistory.map((history) => (
                        <TableRow key={history.id} hover>
                          <TableCell>{fDateTime(history.created)}</TableCell>
                          <TableCell>{history.sender_email}</TableCell>
                          <TableCell>{history.recipient_count}</TableCell>
                          <TableCell>
                            <Chip
                              label={history.delivery_status}
                              size="small"
                              color={
                                history.delivery_status === "sent"
                                  ? "success"
                                  : "default"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!pitchHistory || pitchHistory.length === 0) && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ py: 3, color: "text.secondary" }}
                          >
                            No pitch history found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Preview Dialog */}
        {isPreviewDialogOpen && (
          <Dialog
            maxWidth="md"
            fullWidth
            open={isPreviewDialogOpen}
            onClose={() => setIsPreviewDialogOpen(false)}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 2, pt: 2 }}
            >
              <div></div>
              <Typography variant={matches ? "h6" : "h5"} textAlign="center">
                Pitch Preview
              </Typography>
              <IconButton onClick={() => setIsPreviewDialogOpen(false)}>
                <Close />
              </IconButton>
            </Stack>
            <DialogContent sx={{ px: 3 }}>
              <Box
                mt={1}
                border="1px solid #e0e0e0"
                borderRadius={1}
                sx={{ overflow: "hidden", height: "60vh" }}
              >
                <iframe
                  title="Pitch Preview"
                  srcDoc={pitchText}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              </Box>
            </DialogContent>
          </Dialog>
        )}
      </Container>
    </Page>
  );
}
